import {
  fallbackText,
  getDataFromLink,
  isEmptyText,
  Prismic,
  PrismicLink,
  PrismicText,
  Embed,
  getRichTextEmbedHtml,
} from '@landsbankinn/prismic';
import { ArrowButton, Segment, useMedia } from '@landsbankinn/ui';
import React from 'react';
import { ImageVideo } from '../components/image-video/ImageVideo';
import { Img } from '../components/img/Img';
import { Infogram } from '../components/infogram/Infogram';
import { TextWithImages } from '../components/slices/TextWithImages';

interface IProps {
  title: Prismic.RichText;
  copy: Prismic.RichText;
  align: 'Default' | 'Left' | 'Right';
  template: string;
  aspect_ratio: 'Ar 16:9' | 'Ar 4:3' | 'Ar 1:1';
  fields: IField[];
  link_text: Prismic.RichText;
  link: Prismic.Link;
  link_2_text: Prismic.RichText;
  link_2: Prismic.Link;
  hasContainer?: boolean;
}

interface IField {
  image: Prismic.Image;
  video?: Prismic.Embed;
  embed?: Prismic.RichText;
  infogramid?: Prismic.RichText;
  ar?: 'Ar 16:9' | 'Ar 4:3' | 'Ar 1:1';
  infogram_background?: string;
}

const ImageItem = (
  {
    image,
    video,
    embed,
    infogramid,
    ar = 'Ar 4:3',
    infogram_background,
  }: IField,
  i: number,
) => {
  const isDesktop = useMedia('lg');

  const AspectRatioMap = {
    'Ar 1:1': 'ar_1_1',
    'Ar 4:3': 'ar_4_3',
    'Ar 16:9': 'ar_16_9',
  };

  const aspectRatio = !isDesktop ? 'ar_1_1' : (AspectRatioMap[ar] as any);
  if (infogramid) {
    const source = Prismic.text(infogramid);
    return (
      <Infogram url={source ?? undefined} background={infogram_background} />
    );
  }

  if (embed) {
    const html = getRichTextEmbedHtml(embed);
    return <Embed type="inline" html={html} />;
  }

  if (video) {
    return (
      <ImageVideo
        key={`video-${i}`}
        image={image}
        video={video}
        ar={aspectRatio}
      />
    );
  }
  return <Img key={`image-${i}`} src={image} ar={aspectRatio} />;
};

export const TextWithImagesContainer = ({
  title,
  copy,
  fields,
  align,
  aspect_ratio,
  link_text,
  link,
  link_2_text,
  link_2,
  hasContainer = true,
}: IProps) => {
  if (!fields) {
    return null;
  }

  const copyText = isEmptyText(copy) ? null : <PrismicText value={copy} />;

  const linkdata = getDataFromLink(link);
  const linkText = fallbackText(link_text, linkdata.title ?? undefined);

  const link2data = getDataFromLink(link_2);
  const link2Text = fallbackText(link_2_text, link2data.title ?? undefined);

  const action = link ? (
    <PrismicLink value={link} title={linkText ?? undefined}>
      <ArrowButton title={linkText ?? ''} isSmall />
    </PrismicLink>
  ) : null;

  const action2 = link_2 ? (
    <PrismicLink value={link_2} title={link2Text ?? undefined}>
      <ArrowButton title={link2Text ?? ''} isSmall />
    </PrismicLink>
  ) : null;

  return (
    <Segment container={hasContainer} className="text-images">
      <TextWithImages
        images={fields?.slice(0, 1).map((item, index) => (
          <ImageItem key={`image-${index}`} ar={aspect_ratio} {...item} />
        ))}
        title={Prismic.text(title)}
        copy={copyText}
        align={align}
        link={action}
        link2={action2}
      />
    </Segment>
  );
};
